import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import '../styles/Dashboard.css';

function Dashboard({ onClose, onNavigate }) {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchDashboardData();
    const interval = setInterval(fetchDashboardData, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await api.get('/dashboard');
      if (response.data.success) {
        setDashboardData(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const claimQuest = async (questType) => {
    try {
      const response = await api.post('/quests/claim', { questType });
      if (response.data.success) {
        alert(`Quest completed! +${response.data.reward_amount} ${response.data.reward_type}`);
        fetchDashboardData(); // Refresh
      } else {
        alert(response.data.error || 'Failed to claim quest');
      }
    } catch (error) {
      alert('Failed to claim quest');
    }
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-loading">Loading dashboard...</div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-error">Failed to load dashboard</div>
      </div>
    );
  }

  const { user: userData, balance, limits, streak, quests, powerups, badge } = dashboardData;

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>Dashboard</h1>
        <button className="dashboard-close" onClick={onClose}>✕</button>
      </div>

      <div className="dashboard-tabs">
        <button
          className={activeTab === 'overview' ? 'active' : ''}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
          className={activeTab === 'quests' ? 'active' : ''}
          onClick={() => setActiveTab('quests')}
        >
          Quests ({quests.filter(q => q.completed && !q.claimed).length})
        </button>
        <button
          className={activeTab === 'powerups' ? 'active' : ''}
          onClick={() => setActiveTab('powerups')}
        >
          Power-Ups ({powerups.length})
        </button>
      </div>

      <div className="dashboard-content">
        {activeTab === 'overview' && (
          <div className="overview-section">
            {/* User Info */}
            <div className="dashboard-card user-card">
              <h3>@{userData.username}</h3>
              <div className="balance-display">
                <span className="balance-amount">{balance}</span>
                <span className="balance-label">Lit-Bits</span>
              </div>
            </div>

            {/* Daily Limits */}
            <div className="dashboard-card limits-card">
              <h3>Daily Limits</h3>
              <div className="limit-row">
                <span>Free Swipes</span>
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{ width: `${(limits.swipes_remaining / limits.swipes_total) * 100}%` }}
                  />
                </div>
                <span className="limit-text">{limits.swipes_remaining}/{limits.swipes_total}</span>
              </div>
              <div className="limit-row">
                <span>Free Votes</span>
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{ width: `${(limits.votes_remaining / limits.votes_total) * 100}%` }}
                  />
                </div>
                <span className="limit-text">{limits.votes_remaining}/{limits.votes_total}</span>
              </div>
              {(limits.bonus_swipes > 0 || limits.bonus_votes > 0) && (
                <div className="bonus-info">
                  Bonus: +{limits.bonus_swipes} swipes, +{limits.bonus_votes} votes
                </div>
              )}
            </div>

            {/* Login Streak */}
            <div className="dashboard-card streak-card">
              <h3>🔥 Login Streak</h3>
              <div className="streak-display">
                <span className="streak-number">{streak.current_streak}</span>
                <span className="streak-label">days</span>
              </div>
              {streak.next_milestone && (
                <div className="next-milestone">
                  <p>Next reward at {streak.next_milestone.days} days:</p>
                  <p className="milestone-reward">
                    +{streak.next_milestone.bonus_swipes} swipes
                    {streak.next_milestone.bonus_votes > 0 && `, +${streak.next_milestone.bonus_votes} votes`}
                  </p>
                </div>
              )}
            </div>

            {/* Aura Badge */}
            {badge.aura_stats && (
              <div className="dashboard-card badge-card">
                <h3>Aura Status</h3>
                <div className="badge-display">
                  {badge.tier ? (
                    <>
                      <div className="badge-tier">{badge.tier.toUpperCase()}</div>
                      <div className="badge-percent">{badge.aura_stats.auraPercent}%</div>
                    </>
                  ) : (
                    <div className="no-badge">No badge yet - start swiping!</div>
                  )}
                </div>
                <div className="aura-stats">
                  <span>🔥 {badge.aura_stats.fireSwipes} Fire</span>
                  <span>👈 {badge.aura_stats.leftSwipes} Left</span>
                </div>
              </div>
            )}

            {/* Quick Actions */}
            <div className="dashboard-card actions-card">
              <h3>Quick Actions</h3>
              <div className="action-buttons">
                <button onClick={() => onNavigate('aura')}>Start Swiping</button>
                <button onClick={() => onNavigate('voting')}>Cast Votes</button>
                <button onClick={() => onNavigate('boost')}>Buy Power-Ups</button>
                <button onClick={() => onNavigate('profile', userData.username)}>My Profile</button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'quests' && (
          <div className="quests-section">
            <h2>Daily Quests</h2>
            {quests.length === 0 ? (
              <div className="no-quests">No quests available. Check back tomorrow!</div>
            ) : (
              <div className="quests-list">
                {quests.map((quest, index) => (
                  <div key={index} className={`quest-card ${quest.completed ? 'completed' : ''} ${quest.claimed ? 'claimed' : ''}`}>
                    <div className="quest-icon">{quest.icon}</div>
                    <div className="quest-info">
                      <h4>{quest.display_name}</h4>
                      <p>{quest.description}</p>
                      <div className="quest-progress">
                        <div className="progress-bar">
                          <div
                            className="progress-fill"
                            style={{ width: `${Math.min((quest.progress / quest.target) * 100, 100)}%` }}
                          />
                        </div>
                        <span>{quest.progress}/{quest.target}</span>
                      </div>
                      <div className="quest-reward">
                        Reward: {quest.reward_amount} {quest.reward_type.replace('_', ' ')}
                      </div>
                    </div>
                    <div className="quest-action">
                      {quest.claimed ? (
                        <span className="claimed-badge">✓ Claimed</span>
                      ) : quest.completed ? (
                        <button onClick={() => claimQuest(quest.quest_type)}>Claim</button>
                      ) : (
                        <span className="in-progress">In Progress</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'powerups' && (
          <div className="powerups-section">
            <h2>Active Power-Ups</h2>
            {powerups.length === 0 ? (
              <div className="no-powerups">
                <p>No active power-ups</p>
                <button onClick={() => onNavigate('boost')}>Visit Shop</button>
              </div>
            ) : (
              <div className="powerups-list">
                {powerups.map((powerup, index) => {
                  const timeRemaining = powerup.expires_at ? Math.max(0, powerup.expires_at - Date.now()) : null;
                  const minutesRemaining = timeRemaining ? Math.floor(timeRemaining / 60000) : null;

                  return (
                    <div key={index} className="powerup-card">
                      <div className="powerup-type">{powerup.powerup_type.replace('_', ' ').toUpperCase()}</div>
                      {minutesRemaining !== null ? (
                        <div className="powerup-timer">
                          {minutesRemaining > 60
                            ? `${Math.floor(minutesRemaining / 60)}h ${minutesRemaining % 60}m remaining`
                            : `${minutesRemaining}m remaining`
                          }
                        </div>
                      ) : powerup.quantity ? (
                        <div className="powerup-quantity">{powerup.quantity} charges left</div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Dashboard;
